import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { faAssign } from '@awesome.me/kit-d87edcad8c/icons/kit/custom';
import { AnyAbility } from '@casl/ability';
import { AbilityServiceSignal, AblePurePipe } from '@casl/angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faArrowsLeftRight, faMagnet } from '@fortawesome/pro-solid-svg-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  RoleAction,
  RolePermissionSubject,
  ZOOM_LEVEL,
} from '@wilson/interfaces';
import { ShiftTimelinePreferredSettingsState } from '@wilson/preferred-settings/state';
import { DragAndDropModeService } from '@wilson/shift-timeline/services';
import { ShiftTimelineSettingsState } from '@wilson/shift-timeline/state';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { filter, firstValueFrom, Observable, switchMap } from 'rxjs';

@Component({
  selector: 'wilson-drag-drop-mode-action-bar',
  templateUrl: './drag-drop-mode-action-bar.component.html',
  styleUrls: ['./drag-drop-mode-action-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    NzTooltipModule,
    FeatureFlagsModule,
    FontAwesomeModule,
  ],
})
export class DragDropModeActionBarComponent implements OnInit {
  private readonly store = inject(Store);
  readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly ablePurePipe =
    inject<AblePurePipe<AnyAbility>>(AblePurePipe);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translate = inject(TranslateService);

  readonly faAssign = faAssign;
  readonly faSnappingIcon = faMagnet;
  readonly faArrowsLeftRight = faArrowsLeftRight;
  can = inject(AbilityServiceSignal).can;

  @Input() isActionCardOpen = true;

  @HostListener('document:keydown.a')
  toggleAssignmentMode(): void {
    this.dragAndDropModeService.toggleAssignmentMode();
  }

  @HostListener('document:keydown.v')
  async toggleMoveMode(): Promise<void> {
    const hasShiftUpdatePermission = await firstValueFrom(
      this.ablePurePipe.transform(
        RoleAction.Update,
        RolePermissionSubject.Shift,
      ),
    );
    const hasAbsenceUpdatePermission = await firstValueFrom(
      this.ablePurePipe.transform(
        RoleAction.Update,
        RolePermissionSubject.Absence,
      ),
    );
    if (hasShiftUpdatePermission && hasAbsenceUpdatePermission) {
      this.dragAndDropModeService.toggleMoveMode();
    } else {
      this.nzMessageService.warning(
        this.translate.instant('general.forbidden_action'),
      );
    }
  }
  @HostListener('window:keydown.control.m')
  async toggleSnappingMode(): Promise<void> {
    const isMoveMode = await firstValueFrom(
      this.dragAndDropModeService.isMoveMode$,
    );
    if (isMoveMode) {
      const zoomLevel = this.store.selectSnapshot(
        ShiftTimelineSettingsState.zoomLevel,
      );
      this.dragAndDropModeService.toggleSnapping(zoomLevel);
    }
  }

  zoomLevel$ = this.store.select(ShiftTimelineSettingsState.zoomLevel);
  isCellView$!: Observable<boolean>;
  ZOOM_LEVEL = ZOOM_LEVEL;
  RoleAction = RoleAction;
  RolePermissionSubject = RolePermissionSubject;

  ngOnInit(): void {
    this.isCellView$ = this.store.select(AuthState.userId).pipe(
      filter((userId) => !!userId),
      switchMap((userId) =>
        this.store.select(
          ShiftTimelinePreferredSettingsState.isCellView(userId as string),
        ),
      ),
    );
  }
}
